<template>
    <v-form @submit.prevent="onSubmit" ref="form">
        <v-card outlined :loading="isBusy">
            <v-card-text>
                <v-row>
                    <!-- buyer type -->
                    <v-col cols="12" class="py-0">
                        <p class="overline">{{ $t("warehouses.buyer_type") }}</p>
                    </v-col>
                    <v-col cols="12" class="py-0">
                        <v-select v-model="form.buyer_type"
                                  :label="$t('warehouses.buyer_type')"
                                  required
                                  item-value="external_id"
                                  item-text="name"
                                  :error-messages="buyerTypeErrors"
                                  :loading="isBusyBuyerTypes"
                                  :items="buyerTypes"
                                  :rules="[rules.required]"
                                  :disabled="isBusy"
                        />
                    </v-col>
                    <!-- carrier -->
                    <v-col cols="12" class="py-0">
                        <p class="overline">{{ $t("warehouses.carrier_service") }}</p>
                    </v-col>
                    <v-col cols="12" class="py-0">
                        <v-select v-model="form.carrier_service"
                                  :label="$t('warehouses.carrier_service')"
                                  required
                                  :error-messages="carrierServiceErrors"
                                  :loading="isBusyCarriers"
                                  :items="carrierServices"
                                  :rules="[rules.required]"
                                  :disabled="isBusy"
                                  @input="form.carrier_service_parameter_values = {}"
                        />
                    </v-col>
                    <!-- carrier service parameters -->
                    <template v-if="form.carrier_service">
                        <v-col cols="12" class="py-0">
                            <p class="overline">{{ $t("warehouses.carrier_service_parameters") }}</p>
                        </v-col>
                        <template v-if="carrierServiceParameters.length > 0">
                            <v-col cols="12" v-for="(parameter, i) in carrierServiceParameters" :key="i" class="py-0">
                                <v-text-field
                                    v-model="form.carrier_service_parameter_values[parameter.id]"
                                    :label="parameter.name"
                                    :hint="parameter.required ? $t('warehouses.required') : $t('warehouses.optional')"
                                    :rules="parameterRules(parameter)"
                                    :disabled="isBusy"
                                    persistent-hint
                                />
                            </v-col>
                        </template>
                        <template v-else>
                            <v-col cols="12" class="py-0">
                                <p>
                                    <v-icon>mdi-information-outline</v-icon>
                                    {{ $t("warehouses.no_carrier_service_parameters") }}
                                </p>
                            </v-col>
                        </template>
                    </template>
                    <!-- email notifications -->
                    <v-col cols="12" class="py-0">
                        <v-checkbox v-model="form.send_order_created_emails"
                                    :label="$t('warehouses.send_order_created_emails')"
                                    :true-value="true"
                                    :false-value="false"
                                    hide-details
                        />
                        <v-checkbox v-model="form.send_order_waiting_for_approval_emails"
                                    :label="$t('warehouses.send_order_waiting_for_approval_emails')"
                                    :true-value="true"
                                    :false-value="false"
                                    hide-details
                        />
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions>
                <v-btn type="submit" color="primary" dark class="mt-3" :disabled="isBusy">
                    {{ $t('warehouses.save') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>

<script>
import SnackbarMixin from '@/mixins/SnackbarMixin'
import {API} from '@/api/api'

export default {
    name: "EditWarehouseForm",
    mixins: [SnackbarMixin],
    props: {
        warehouse: {
            type: Object,
            required: true
        }
    },
    data: () => ({
        // loading
        isBusySubmit: false,
        isBusyBuyerTypes: false,
        isBusyCarriers: false,
        // buyer types
        buyerTypes: [],
        // carriers
        carriers: [],
        // form
        form: {
            buyer_type: null,
            carrier_service: null,
            carrier_service_parameter_values: {},
            send_order_created_emails: true,
            send_order_waiting_for_approval_emails: true
        },
        // form rules
        rules: {
            required: value => !!value || "Tento parametr je povinný"
        }
    }),
    computed: {
        isBusy() {
            return this.isBusySubmit || this.isBusyBuyerTypes || this.isBusyCarriers;
        },
        carrierServices() {
            let items = [];
            this.carriers.forEach(carrier => {
                if (carrier.carrier_services.length > 0) {
                    items.push({header: carrier.name});
                    carrier.carrier_services.forEach(carrierService => {
                        items.push({value: carrierService.id, text: carrierService.name});
                    });
                }
            })
            return items;
        },
        carrierServiceParameters() {
            if (this.form.carrier_service === null) {
                return [];
            }
            let carrierService = null;
            this.carriers.forEach(carrier => {
                carrier.carrier_services.forEach(service => {
                    if (service.id === this.form.carrier_service) {
                        carrierService = service
                    }
                })
            })
            if (carrierService === null) {
                return [];
            }
            let parameters = carrierService.carrier_service_parameters;
            parameters.sort(x => x.required ? -1 : 1);
            return parameters;
        },
        buyerTypeErrors() {
            return []; // TODO: implement me
        },
        carrierServiceErrors() {
            return []; // TODO: implement me
        }
    },
    methods: {
        onSubmit() {
            if (!this.$refs['form'].validate()) {
                return;
            }

            const request = {
                buyer_type: this.form.buyer_type,
                carrier_service: this.form.carrier_service,
                carrier_service_parameter_values: Object.entries(this.form.carrier_service_parameter_values)
                    .map(entry => ({
                        carrier_service_parameter: entry[0],
                        carrier_service_parameter_value: entry[1]
                    })),
                send_order_created_emails: this.form.send_order_created_emails,
                send_order_waiting_for_approval_emails: this.form.send_order_waiting_for_approval_emails
            }

            this.isBusySubmit = true;
            API.put("/api/warehouses/" + this.warehouse.id, request)
                .then(() => {
                    this.snackbarSuccess(this.$t("snackbar.warehouses.updated"));
                    this.$emit("onSubmit");
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusySubmit = false;
                });
        },
        parameterRules(parameter) {
            return parameter.required ? [this.rules.required] : []
        },
        fetchBuyerTypesByWarehouse() {
            this.isBusyBuyerTypes = true;
            API.get("/api/buyer-types/by-warehouse/" + this.warehouse.id)
                .then(response => {
                    this.buyerTypes = response.data;
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyBuyerTypes = false;
                });
        },
        fetchCarriersByWarehouse() {
            this.isBusyCarriers = true;
            API.get("/api/carriers/by-warehouse/" + this.warehouse.id)
                .then(response => {
                    this.carriers = response.data;
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyCarriers = false;
                });
        }
    },
    created() {
        this.fetchBuyerTypesByWarehouse();
        this.fetchCarriersByWarehouse();

        this.$set(this.form, 'buyer_type', this.warehouse?.buyer_type_external_id ?? null);
        this.$set(this.form, 'carrier_service', this.warehouse.carrier_service?.id ?? null);
        this.warehouse.carrier_service_parameter_values.forEach(value => {
            this.$set(this.form.carrier_service_parameter_values, value.carrier_service_parameter.id, value.value);
        });
        this.$set(this.form, 'send_order_created_emails', this.warehouse.send_order_created_emails);
        this.$set(this.form, 'send_order_waiting_for_approval_emails', this.warehouse.send_order_waiting_for_approval_emails);
    }
}
</script>
